
import React, { useState } from 'react';
import { Journey, Session, Question, Feedback } from '@shared/types';
import { speakText } from '@shared/utils/voice';

interface Props {
  journeys: Journey[];
  sessions: Session[];
  onBack: () => void;
}

const renderBoldText = (value: string) => {
  const safeValue = value || "";
  const parts = safeValue.split("**");
  return parts.map((part, idx) => idx % 2 === 1 ? (
    <strong key={idx} className="font-black">{part}</strong>
  ) : (
    <span key={idx}>{part}</span>
  ));
};

const PinnedList: React.FC<Props> = ({ journeys, sessions, onBack }) => {
  const [selectedItem, setSelectedItem] = useState<any>(null);

  // Get all pinned questions and their most recent feedback
  const allPinned = journeys.flatMap(j => 
    j.questions?.filter(q => q.isPinned).map(q => {
      const lastAttempt = sessions
        .flatMap(s => s.attempts)
        .filter(a => a.questionId === q.id)
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];

      return { 
        ...q, 
        journeyTitle: j.title, 
        lastAttempt: lastAttempt,
        lastScore: lastAttempt?.feedback?.overallScore,
        lastFeedback: lastAttempt?.feedback
      };
    }) || []
  );

  const closeModal = () => setSelectedItem(null);

  return (
    <div className="p-8 lg:p-12 relative min-h-[700px]">
      <div className="flex justify-between items-center mb-16">
        <div>
          <h1 className="text-4xl font-black text-slate-900 mb-2 tracking-tight">Revision Master List</h1>
          <p className="text-slate-500 font-medium italic">High-yield stations curated for your intensive review.</p>
        </div>
        <button onClick={onBack} className="bg-slate-900 text-white px-8 py-3 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-blue-600 transition-all shadow-lg shadow-slate-200">
          ← Back
        </button>
      </div>

      {allPinned.length === 0 ? (
        <div className="text-center py-32 bg-slate-50 rounded-[4rem] border-4 border-dashed border-slate-100">
          <div className="w-24 h-24 bg-white rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl text-slate-200">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6a3 3 0 0 0-6 0v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/></svg>
          </div>
          <p className="text-slate-400 font-black text-xs uppercase tracking-widest max-w-xs mx-auto leading-relaxed">
            No pinned questions yet. Click the pin icon during an interview session to save important stations here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {allPinned.map((q, i) => (
            <div 
              key={i} 
              className={`bg-white p-10 rounded-[3rem] border border-slate-100 shadow-2xl shadow-slate-200/40 hover:border-blue-300 transition-all group relative overflow-hidden flex flex-col ${q.lastFeedback ? 'hover:-translate-y-1' : ''}`}
            >
              <div className="absolute top-0 right-0 p-8 text-blue-100 group-hover:text-blue-200 transition-colors">
                 <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6a3 3 0 0 0-6 0v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/></svg>
              </div>
              <div className="mb-6 flex justify-between items-start relative z-10">
                <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest bg-blue-50 px-3 py-1 rounded-lg">{q.journeyTitle}</span>
                {q.lastScore !== undefined && (
                  <div className="text-right">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Last Performance</p>
                    <p className={`text-xl font-black ${q.lastScore < 60 ? 'text-red-500' : 'text-emerald-500'}`}>{q.lastScore}%</p>
                  </div>
                )}
              </div>
              
              <div className="relative group/text mb-8">
                <h3 
                  onClick={() => q.lastFeedback && setSelectedItem(q)}
                  className={`text-2xl font-black text-slate-800 leading-tight pr-12 relative z-10 ${q.lastFeedback ? 'cursor-pointer hover:text-blue-600' : ''}`}
                >
                  "{q.text}"
                </h3>
                <button 
                  onClick={(e) => { e.stopPropagation(); speakText(q.text); }}
                  className="absolute top-0 right-0 bg-blue-50 text-blue-500 p-2.5 rounded-xl hover:bg-blue-600 hover:text-white transition-all shadow-sm active:scale-90"
                  title="Listen to Station"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                </button>
              </div>
              
              <div className="flex-grow space-y-4 mb-8">
                {q.lastFeedback?.blindSpots && q.lastFeedback.blindSpots.length > 0 ? (
                  <div className="bg-red-50 p-4 rounded-2xl border border-red-100">
                    <p className="text-[10px] font-black text-red-600 uppercase mb-2 tracking-widest">Mastery Gaps</p>
                    <p className="text-xs text-red-800 font-bold leading-relaxed italic line-clamp-2">"{q.lastFeedback.blindSpots[0]}..."</p>
                  </div>
                ) : (
                  <div className="p-4 rounded-2xl border-2 border-dashed border-slate-100 text-center">
                    <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">No feedback data yet</p>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 mt-auto relative z-10 items-center justify-between">
                <div className="flex flex-wrap gap-2">
                  {q.domainTags?.map(t => (
                    <span key={t} className="text-[10px] font-black bg-slate-50 text-slate-400 px-3 py-1 rounded-full border border-slate-100 uppercase tracking-tighter">{t}</span>
                  ))}
                </div>
                {q.lastFeedback && (
                  <button 
                    onClick={() => setSelectedItem(q)}
                    className="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-1 group-hover:translate-x-1 transition-transform"
                  >
                    View Audit <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Audit Detail Modal */}
      {selectedItem && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-10 animate-in fade-in duration-300">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={closeModal}></div>
          <div className="relative w-full max-w-6xl max-h-[90vh] bg-white rounded-[4rem] shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 slide-in-from-bottom-4 duration-500">
            {/* Modal Header */}
            <div className="p-10 border-b border-slate-100 flex justify-between items-start shrink-0">
              <div className="max-w-3xl">
                <div className="flex items-center gap-3 mb-4">
                  <span className="bg-blue-600 text-white text-[10px] font-black px-4 py-1 rounded-full uppercase tracking-[0.2em]">{selectedItem.journeyTitle}</span>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Historical Performance Audit</span>
                </div>
                <div className="flex items-center gap-4">
                  <h2 className="text-3xl font-black text-slate-900 leading-tight">"{selectedItem.text}"</h2>
                  <button 
                    onClick={() => speakText(selectedItem.text)}
                    className="bg-blue-50 text-blue-600 p-3 rounded-xl hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                  </button>
                </div>
              </div>
              <button onClick={closeModal} className="p-4 bg-slate-100 text-slate-400 hover:text-slate-900 rounded-full transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-grow overflow-y-auto p-10 custom-scrollbar space-y-12">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                {/* Score Card */}
                <div className="lg:col-span-1 space-y-6">
                  <div className="bg-slate-50 border border-slate-100 p-8 rounded-[3rem] text-center">
                    <p className="text-[10px] font-black text-slate-400 uppercase mb-3 tracking-widest">Mastery Level</p>
                    <div className="flex flex-col items-center">
                      <h3 className="text-7xl font-black text-blue-600">{selectedItem.lastScore || '0'}%</h3>
                    </div>
                  </div>

                  <div className="bg-white border border-slate-100 p-8 rounded-[3rem] shadow-sm">
                    <p className="text-[10px] font-black text-slate-400 uppercase mb-6 tracking-widest">Domain Breakdown</p>
                    <div className="space-y-4">
                      {Object.entries(selectedItem.lastFeedback?.scores || {}).map(([domain, score]: [string, any]) => (
                        <div key={domain}>
                          <div className="flex justify-between text-[10px] font-black mb-2 tracking-widest uppercase">
                            <span className="text-slate-500">{domain}</span>
                            <span className="text-blue-600">{score}%</span>
                          </div>
                          <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-600 rounded-full" style={{ width: `${score}%` }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {selectedItem.lastFeedback?.blindSpots?.length > 0 && (
                      <div className="mt-8 border-t border-slate-100 pt-6">
                        <p className="text-[10px] font-black text-red-600 uppercase mb-3 tracking-widest">Mastery Gaps</p>
                        <ul className="space-y-2">
                          {selectedItem.lastFeedback.blindSpots.map((spot: string, idx: number) => (
                            <li key={idx} className="text-xs font-bold text-slate-700 bg-red-50 p-3 rounded-xl border border-red-100 flex gap-2">
                              <span className="text-red-500 font-black">!</span> {renderBoldText(spot)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Feedback Analysis */}
                <div className="lg:col-span-2 space-y-10">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <p className="text-[10px] font-black text-emerald-600 uppercase mb-4 tracking-widest">Key Strengths</p>
                      <ul className="space-y-3">
                        {selectedItem.lastFeedback?.pros?.map((p: string, idx: number) => (
                          <li key={idx} className="text-sm font-bold text-slate-700 bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex gap-3">
                            <span className="text-emerald-500">✓</span> {renderBoldText(p)}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-red-600 uppercase mb-4 tracking-widest">Blind Spots & Omissions</p>
                      <ul className="space-y-3">
                        {selectedItem.lastFeedback?.cons?.map((c: string, idx: number) => (
                          <li key={idx} className="text-sm font-bold text-slate-700 bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex gap-3">
                            <span className="text-red-500 font-black">!</span> {renderBoldText(c)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="bg-slate-900 rounded-[3rem] p-10 text-white shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-8 opacity-5 text-8xl font-black">?</div>
                    <p className="text-[10px] font-black text-blue-400 uppercase mb-6 tracking-widest">Consultant Level Model Answer</p>
                    <div className="text-sm font-medium leading-relaxed italic text-slate-300 bg-white/5 p-8 rounded-[2rem] border border-white/10 shadow-inner">
                      "{renderBoldText(selectedItem.lastFeedback?.suggestedAnswer || 'Gold-standard answer formulation pending...')}"
                    </div>
                  </div>
                </div>
              </div>

              {/* Hurdle Analysis Section */}
              {(selectedItem.lastAttempt?.followUpThread?.[1]?.q || selectedItem.lastFeedback?.hurdleIdealAnswer) && (
                <div className="bg-amber-50/50 border border-amber-100 rounded-[4rem] p-12 relative overflow-hidden">
                   <div className="text-center mb-10">
                      <span className="bg-amber-100 text-amber-700 text-[10px] font-black px-5 py-2 rounded-full uppercase tracking-[0.2em]">Follow-up Defense Audit</span>
                   </div>
                   {selectedItem.lastAttempt?.followUpThread?.[1]?.q && (
                     <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
                       <div className="bg-white p-6 rounded-3xl border border-amber-200">
                         <p className="text-[9px] font-black text-amber-600 uppercase mb-2 tracking-widest">Hurdle Question</p>
                         <p className="text-sm font-bold text-slate-700 italic">"{selectedItem.lastAttempt.followUpThread[1].q}"</p>
                       </div>
                       <div className="bg-white p-6 rounded-3xl border border-amber-200">
                         <p className="text-[9px] font-black text-amber-600 uppercase mb-2 tracking-widest">Your Response</p>
                         <p className="text-sm font-bold text-slate-700 italic">
                           "{selectedItem.lastAttempt.followUpThread[1].a || 'No response captured.'}"
                         </p>
                       </div>
                     </div>
                   )}
                   <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                      <div className="space-y-6">
                        <div className="bg-white p-6 rounded-3xl border border-amber-200">
                           <p className="text-[9px] font-black text-amber-600 uppercase mb-2 tracking-widest">Defense Strengths</p>
                           <ul className="space-y-2">
                             {selectedItem.lastFeedback.hurdlePros?.map((p: string, i: number) => (
                               <li key={i} className="text-xs font-bold text-slate-600 flex gap-2"><span className="text-emerald-500">✓</span> {renderBoldText(p)}</li>
                             ))}
                           </ul>
                        </div>
                        <div className="bg-white p-6 rounded-3xl border border-amber-200">
                           <p className="text-[9px] font-black text-red-600 uppercase mb-2 tracking-widest">Defense Omissions</p>
                           <ul className="space-y-2">
                             {selectedItem.lastFeedback.hurdleCons?.map((c: string, i: number) => (
                               <li key={i} className="text-xs font-bold text-slate-600 flex gap-2"><span className="text-red-500 font-black">!</span> {renderBoldText(c)}</li>
                             ))}
                           </ul>
                        </div>
                      </div>
                      <div className="bg-slate-900 rounded-[3rem] p-10 text-white shadow-xl flex flex-col justify-center">
                         <p className="text-[10px] font-black text-amber-400 uppercase mb-4 tracking-widest">Ideal Consultant Rebuttal</p>
                         <p className="text-sm font-medium italic text-slate-300 leading-relaxed bg-white/5 p-6 rounded-2xl border border-white/10">
                           "{renderBoldText(selectedItem.lastFeedback.hurdleIdealAnswer)}"
                         </p>
                      </div>
                   </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-8 border-t border-slate-100 bg-slate-50 flex justify-end shrink-0">
              <button 
                onClick={closeModal} 
                className="bg-slate-900 text-white px-12 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-blue-600 transition-all shadow-xl"
              >
                Close Audit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PinnedList;
